import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import GurujiChatWidget from '../components/GurujiChatWidget';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Users2, Trophy, BookMarked, UserCheck, IndianRupee, Library, Star } from 'lucide-react';
import { whyChooseUs, testimonials } from '../mock';

const iconMap = {
  Users2, Trophy, BookMarked, UserCheck, IndianRupee, Library
};

const About = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Page Header */}
      <section className="bg-gradient-to-b from-red-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">About Vidya365</h1>
            <p className="text-xl text-gray-600">
              Your trusted partner in achieving government job success with expert guidance and proven methodology
            </p>
          </div>
        </div>
      </section>

      {/* Founder's Message Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-8 md:p-12 border-2 border-red-100 shadow-lg">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Founder's Message</h2>
                <div className="w-24 h-1 bg-red-600 mx-auto"></div>
              </div>
              
              <div className="grid md:grid-cols-3 gap-8 mb-8">
                {/* Founder Photo */}
                <div className="md:col-span-1 flex flex-col items-center">
                  <div className="relative">
                    <div className="w-64 h-64 rounded-2xl overflow-hidden shadow-xl border-4 border-white">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_vidya365-coaching/artifacts/lmxorrc1_hqdefault.jpg" 
                        alt="Mr. Sunderpal Dahiya - Founder, Vidya365"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-6 py-2 rounded-full shadow-lg">
                      <p className="font-bold text-sm">Founder</p>
                    </div>
                  </div>
                  <div className="mt-8 text-center">
                    <p className="text-xl font-bold text-gray-900">Mr. Sunderpal Dahiya</p>
                    <p className="text-red-600 font-medium">Founder, Vidya365</p>
                  </div>
                </div>
                
                {/* Message Content */}
                <div className="md:col-span-2 prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
                  <p className="text-xl font-semibold text-red-600">Welcome to Vidya365.</p>
                  
                  <p>
                    Education is not just about acquiring knowledge—it is about building confidence, shaping character, 
                    and unlocking the potential within every individual. When I envisioned Vidya365, my goal was simple 
                    yet powerful: to create a platform where learning is accessible, continuous, and meaningful for everyone, 
                    every day.
                  </p>
                  
                  <p>
                    In today's fast-changing world, the need for quality education has never been greater. Vidya365 is 
                    built on the belief that learning should not be limited by boundaries of time, place, or background. 
                    Whether you are a student striving for excellence, a professional seeking growth, or a curious mind 
                    eager to explore, this platform is designed to support your journey.
                  </p>
                  
                  <p>
                    We are committed to delivering reliable, engaging, and practical learning experiences that empower 
                    you to achieve your goals. Every resource, every lesson, and every effort behind Vidya365 reflects 
                    our dedication to making education simple, effective, and impactful.
                  </p>
                  
                  <p>
                    This is more than just a website—it is a community of learners and dreamers who believe in growing 
                    every single day.
                  </p>
                  
                  <p className="text-lg font-semibold text-gray-900">
                    Thank you for being a part of this journey.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose Vidya365?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Your success is our mission - here's what makes us different from others
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {whyChooseUs.map((item) => {
              const Icon = iconMap[item.icon];
              return (
                <div key={item.id} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow border border-gray-100">
                  <div className="bg-red-100 p-4 rounded-lg w-fit mb-4">
                    <Icon className="text-red-500" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Mission Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Mission</h2>
                <p className="text-lg text-gray-600 mb-4">
                  At Vidya365, we are committed to transforming the lives of aspiring government job seekers through 
                  quality education, expert mentorship, and comprehensive preparation strategies.
                </p>
                <p className="text-lg text-gray-600 mb-6">
                  We believe that every student has the potential to succeed, and our role is to provide them with 
                  the right guidance, resources, and support to achieve their dreams.
                </p>
                <Link to="/contact">
                  <Button className="bg-red-500 hover:bg-red-600 text-white px-8 py-6 text-lg">
                    Join Us Today
                  </Button>
                </Link>
              </div>
              <div className="bg-red-500 text-white p-8 rounded-lg">
                <h3 className="text-2xl font-bold mb-6">Our Achievements</h3>
                <div className="space-y-6">
                  <div>
                    <div className="text-4xl font-bold mb-2">10,000+</div>
                    <p className="text-red-100">Students Trained Successfully</p>
                  </div>
                  <div>
                    <div className="text-4xl font-bold mb-2">85%</div>
                    <p className="text-red-100">Overall Success Rate</p>
                  </div>
                  <div>
                    <div className="text-4xl font-bold mb-2">50+</div>
                    <p className="text-red-100">Experienced Faculty Members</p>
                  </div>
                  <div>
                    <div className="text-4xl font-bold mb-2">12+</div>
                    <p className="text-red-100">Years of Excellence</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Success Stories</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Hear from our successful students who achieved their dreams with Vidya365
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center">
                      <span className="text-2xl font-bold text-red-500">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <CardTitle className="text-lg">{testimonial.name}</CardTitle>
                      <CardDescription className="text-sm">{testimonial.exam}</CardDescription>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={16} className="fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 italic">"{testimonial.text}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Floating Guruji AI Chat Widget */}
      <GurujiChatWidget />
    </div>
  );
};

export default About;
